import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, ChevronRight, BarChart3 } from 'lucide-react'
import { api } from '../../lib/api'
import { Table, type Colonna } from '../../components/ui/Table'
import { Badge } from '../../components/ui/Badge'
import { Button } from '../../components/ui/Button'
import { EmptyState } from '../../components/ui/EmptyState'
import { formatDataOra, formatDurata } from '../../lib/format'
import pagina from '../pagine.module.css'

type FeedbackStato = 'nessuno' | 'in_elaborazione' | 'completato' | 'errore'

interface LeadInfo {
  id: string
  nome: string
  ruolo: string | null
  settore: string | null
}

interface Sessione {
  id: string
  durataSecondi: number
  votoComplessivo: number | null
  metodoAvvio: 'preselezionato' | 'ivr' | 'browser' | null
  completata: boolean
  feedbackStato: FeedbackStato
  feedbackLetto: boolean
  /** Snapshot lead dal backend (null se eliminato o non associato) */
  lead: LeadInfo | null
  /** true/false/null: null = valutazione non disponibile per sessioni vecchie */
  obiettivoRaggiunto: boolean | null
  createdAt: string
}

interface Risposta {
  sessioni: Sessione[]
  totale: number
  page: number
  per_pagina: number
}

export function SdrSessioni() {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)

  const q = useQuery({
    queryKey: ['sdr', 'sessioni', page],
    queryFn: async () =>
      (await api.get<{ dati: Risposta }>('/sessions', { params: { page, per_pagina: 20 } })).data.dati,
    // Polling 10s SOLO finché c'è almeno una sessione con feedback in
    // elaborazione: appena tutti sono pronti il polling si ferma.
    refetchInterval: (query) => {
      const d = query.state.data
      const inAttesa = d?.sessioni.some((s) => s.feedbackStato === 'in_elaborazione')
      return inAttesa ? 10_000 : false
    },
  })

  if (q.isLoading) return <div className={pagina.skeleton}>Caricamento…</div>
  if (q.error || !q.data) return <EmptyState titolo="Errore caricamento" />

  const colonne: Colonna<Sessione>[] = [
    {
      chiave: 'data',
      intestazione: 'Data',
      render: (s) => formatDataOra(s.createdAt),
      larghezza: '180px',
    },
    {
      chiave: 'lead',
      intestazione: 'Lead',
      render: (s) => <CellaLead lead={s.lead} />,
      larghezza: '200px',
    },
    {
      chiave: 'durata',
      intestazione: 'Durata',
      render: (s) => <span className="mono">{formatDurata(s.durataSecondi)}</span>,
      larghezza: '100px',
    },
    {
      chiave: 'metodo',
      intestazione: 'Avvio',
      render: (s) => {
        const m = s.metodoAvvio
        const etichetta = m === 'browser' ? '🎙 Browser' : m === 'preselezionato' ? 'App' : 'IVR'
        return (
          <Badge variante={m === 'browser' ? 'accent' : m === 'preselezionato' ? 'info' : 'default'}>
            {etichetta}
          </Badge>
        )
      },
      larghezza: '110px',
    },
    {
      chiave: 'voto',
      intestazione: 'Voto',
      allineamento: 'right',
      render: (s) =>
        s.votoComplessivo != null ? (
          <span className="mono" style={{ fontWeight: 600 }}>
            {s.votoComplessivo}/10
          </span>
        ) : (
          <span style={{ color: 'var(--text-muted)' }}>—</span>
        ),
      larghezza: '90px',
    },
    {
      chiave: 'obiettivo',
      intestazione: 'Obiettivo',
      render: (s) => <BadgeObiettivo raggiunto={s.obiettivoRaggiunto} />,
      larghezza: '160px',
    },
    {
      chiave: 'stato',
      intestazione: 'Feedback',
      render: (s) => <StatoFeedback sessione={s} />,
      larghezza: '180px',
    },
  ]

  const totalePagine = Math.max(1, Math.ceil(q.data.totale / q.data.per_pagina))

  return (
    <div className={pagina.wrap}>
      <div className={pagina.header}>
        <div>
          <h1 className={pagina.titolo}>Le mie sessioni</h1>
          <div className={pagina.sottotitolo}>
            {q.data.totale} sessioni totali — clicca una riga per vedere trascritto e feedback
          </div>
        </div>
      </div>

      <Table
        colonne={colonne}
        righe={q.data.sessioni}
        onClickRiga={(s) => navigate(`/app/sessioni/${s.id}`)}
        vuoto={
          <EmptyState
            icona={<BarChart3 size={40} />}
            titolo="Nessuna sessione ancora"
            descrizione="Chiama il numero RingCoach con un lead pre-selezionato per iniziare."
          />
        }
      />

      {/* paginazione */}
      {totalePagine > 1 && (
        <div className={pagina.paginazione}>
          <span>Pagina {q.data.page} di {totalePagine}</span>
          <div className={pagina.paginazioneBottoni}>
            <Button
              taglia="sm"
              variante="secondary"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
              iconaSinistra={<ChevronLeft size={14} />}
            >
              Indietro
            </Button>
            <Button
              taglia="sm"
              variante="secondary"
              disabled={page >= totalePagine}
              onClick={() => setPage(page + 1)}
              iconaDestra={<ChevronRight size={14} />}
            >
              Avanti
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Indicatore di stato del feedback per la riga della lista sessioni.
 * Quattro casi:
 *  - 'completato' + non letto  → badge "Nuovo feedback" verde (call-to-action)
 *  - 'completato' + letto      → "Completata" neutro
 *  - 'in_elaborazione'         → spinner inline "in elaborazione…"
 *  - 'errore'                  → "Feedback non disponibile" (l'utente può
 *                                rigenerare dal dettaglio)
 *  - 'nessuno' + completata    → "Sessione breve" (nessun feedback generato)
 *  - !completata               → "Incompleta"
 */
function StatoFeedback({ sessione: s }: { sessione: Sessione }) {
  if (!s.completata) return <Badge variante="warning">Incompleta</Badge>

  if (s.feedbackStato === 'completato' && !s.feedbackLetto) {
    return <Badge variante="success">● Nuovo feedback</Badge>
  }
  if (s.feedbackStato === 'completato') {
    return <Badge variante="default">Completata</Badge>
  }
  if (s.feedbackStato === 'in_elaborazione') {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-muted)' }}>
        <span
          style={{
            width: 12, height: 12,
            border: '2px solid var(--border)',
            borderTopColor: 'var(--accent)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            display: 'inline-block',
          }}
        />
        In elaborazione…
      </span>
    )
  }
  if (s.feedbackStato === 'errore') {
    return (
      <span style={{ fontSize: 12, color: 'var(--warning)' }}>
        ⚠ Feedback non disponibile
      </span>
    )
  }
  // 'nessuno': sessione completata ma troppo breve per il feedback
  return <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Sessione breve</span>
}

/**
 * Nome del lead usato nella sessione. Fallback quando il record
 * `lead_profiles` è stato eliminato o la sessione non aveva lead
 * associato (es. sessioni di test molto vecchie).
 */
function CellaLead({ lead }: { lead: LeadInfo | null }) {
  if (!lead) {
    return <span style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>Lead non disponibile</span>
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <span style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {lead.nome}
      </span>
      {(lead.ruolo || lead.settore) && (
        <span style={{ fontSize: 11, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {[lead.ruolo, lead.settore].filter(Boolean).join(' · ')}
        </span>
      )}
    </div>
  )
}

/**
 * Badge esito obiettivo per la lista sessioni. Tre stati:
 *  - true → verde "Obiettivo raggiunto"
 *  - false → rosso "Non raggiunto"
 *  - null → neutro "N/D" (sessioni pre-feature obiettivi o senza obiettivo scelto)
 */
function BadgeObiettivo({ raggiunto }: { raggiunto: boolean | null }) {
  if (raggiunto === true) return <Badge variante="success">✓ Raggiunto</Badge>
  if (raggiunto === false) return <Badge variante="danger">✘ Non raggiunto</Badge>
  return <Badge variante="default">N/D</Badge>
}
